import { useForm } from "react-hook-form"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Textarea } from "@components/ui/textarea"
import { Button } from "@components/ui/button"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import {
  useGetAvailablePromosFilteredQuery,
  useGetPromoReasonsSerializedQuery,
} from "@apis/rewards/useRewards"
import { useAddPromo } from "@apis/accounts/useAccounts"
import { type IAddPromoForm } from "@apis/rewards/types"

type TAddPromoProps = {
  account_id: string
}

export const AddPromo = ({ account_id }: TAddPromoProps) => {
  const form = useForm<IAddPromoForm>({
    mode: "onTouched",
    defaultValues: { promo_code: "", reason_code: "", note: "" },
  })
  const { data: promos = [] } = useGetAvailablePromosFilteredQuery("P0M")
  const { data: reasons = [] } = useGetPromoReasonsSerializedQuery()
  const { mutate, isPending } = useAddPromo()

  const onSubmit = (data: IAddPromoForm) => {
    mutate(
      {
        account_id,
        data,
      },
      {
        onSuccess: () => {
          toast.success("Successfully added promo!")
          form.reset()
        },
        onError: (error) => toast.error(`Error adding promo! ${error}`),
      },
    )
  }

  return (
    <div className="tw:flex tw:flex-col tw:rounded-lg tw:border tw:border-gray-200 tw:p-4">
      <h2 className="tw:text-xl">Add Promo</h2>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="tw:space-y-6">
          <FormField
            control={form.control}
            name="promo_code"
            rules={{ required: "Promo is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Promo Code</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="tw:w-full">
                      <SelectValue placeholder="Select Promo" />
                    </SelectTrigger>
                    <SelectContent>
                      {promos.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="reason_code"
            rules={{ required: "Reason is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reason</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger className="tw:w-full">
                      <SelectValue placeholder="Select Reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {reasons.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="note"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Note</FormLabel>
                <FormControl>
                  <Textarea {...field} placeholder="Optional" rows={4} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="tw:text-green-600">Found {promos.length} promos</div>

          <Button type="submit" disabled={!form.formState.isValid || isPending}>
            {isPending && (
              <Loader2 className="tw:mr-2 tw:h-4 tw:w-4 tw:animate-spin" />
            )}
            Add Promo
          </Button>
        </form>
      </Form>
    </div>
  )
}
